import { useEffect, useState } from "react";
import { Check, ChevronDown, ChevronUp, Circle, ClipboardCheck, Loader2, TriangleAlert } from "lucide-react";

/** ok | warn | todo | wait */
function Row({ status, title, hint, testId }) {
  const icon = {
    ok: <Check className="h-3.5 w-3.5" />,
    warn: <TriangleAlert className="h-3.5 w-3.5" />,
    wait: <Loader2 className="h-3.5 w-3.5 animate-spin" />,
    todo: <Circle className="h-3 w-3" />,
  }[status];
  const tone = {
    ok: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300",
    warn: "border-amber-500/40 bg-amber-500/15 text-amber-300",
    wait: "border-white/15 bg-white/5 text-zinc-300",
    todo: "border-white/15 bg-white/5 text-zinc-500",
  }[status];
  return (
    <li className="flex items-start gap-3" data-testid={testId} data-status={status}>
      <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${tone}`}>{icon}</span>
      <div className="min-w-0">
        <p className={`text-sm font-medium ${status === "ok" ? "text-zinc-300 line-through decoration-zinc-600" : "text-zinc-100"}`}>{title}</p>
        {hint && status !== "ok" && <p className="text-xs leading-5 text-zinc-400">{hint}</p>}
      </div>
    </li>
  );
}

/**
 * Live pre-show checklist for the producer console. Every row is derived from real
 * state (room, devices, program, roster, recording) — nothing is ticked by hand.
 */
export function PreShowChecklist({ joined, roster, program, participants, show, rtkError }) {
  const rec = show?.recording || {};
  const live = show?.livestream || {};
  const recActive = ["starting", "recording"].includes(rec.status);
  const liveActive = ["starting", "live"].includes(live.status);
  const rolling = recActive || liveActive;

  const self = roster.list.find((r) => r.isSelf);
  const sources = program?.sources || [];
  const onAir = sources.filter((id) => roster.byCustomId.has(id));
  const onAirPeople = participants.filter((p) => onAir.includes(p.participant_id) && p.kind !== "camera");
  const missingPlates = onAirPeople.filter((p) => !(p.title || p.org));
  const guestsInRoom = roster.list.filter((r) => !r.isSelf).length;

  // Recorder connection grace: only warn ~25s after Record/Go live started.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!rolling || show?.program_connected) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [rolling, show?.program_connected]);
  const startedAt = new Date(rec.started_at || live.started_at || 0).getTime();
  const waitedTooLong = rolling && !show?.program_connected && startedAt > 0 && now - startedAt > 25000;

  const rows = [
    {
      id: "room",
      status: rtkError ? "warn" : joined ? "ok" : "wait",
      title: "You're in the room",
      hint: rtkError ? "Couldn't connect — reload the console." : "Connecting to the studio room…",
    },
    {
      id: "devices",
      status: !joined ? "todo" : self?.videoEnabled && self?.audioEnabled ? "ok" : "warn",
      title: "Your camera and mic are on",
      hint: !joined ? "Waits for the room." : !self?.videoEnabled && !self?.audioEnabled ? "Both are off — turn them on in Your camera & mic below." : !self?.videoEnabled ? "Camera is off." : "Mic is muted.",
    },
    {
      id: "guests",
      status: guestsInRoom > 0 ? "ok" : "todo",
      title: guestsInRoom > 0 ? `${guestsInRoom} guest${guestsInRoom === 1 ? "" : "s"} / camera${guestsInRoom === 1 ? "" : "s"} in the room` : "Guests or phone cameras joined",
      hint: "Optional — send the invite or camera link below. Solo shows are fine.",
    },
    {
      id: "source",
      status: onAir.length > 0 ? "ok" : sources.length > 0 ? "warn" : "todo",
      title: "A source is on air",
      hint: sources.length > 0 ? "The selected source isn't in the room anymore — click a live tile." : "Click a tile (or press 1) so the program isn't a black screen.",
    },
    {
      id: "plates",
      status: onAirPeople.length === 0 ? "todo" : missingPlates.length === 0 ? "ok" : "warn",
      title: "On-air name plates have titles",
      hint: onAirPeople.length === 0 ? "Checks whoever is on air." : `${missingPlates.map((p) => p.name).join(", ")} — add a title/org (pencil on the tile) so the lower third isn't just a name.`,
    },
    {
      id: "record",
      status: recActive ? "ok" : liveActive || ["uploading", "imported"].includes(rec.status) ? "warn" : "todo",
      title: recActive ? "Recording" : ["uploading", "imported"].includes(rec.status) ? "Recording stopped" : "Press Record before you start",
      hint: liveActive && !recActive ? "You're live but not recording — press Record so the show is saved." : ["uploading", "imported"].includes(rec.status) ? "Press Record again if the show isn't over." : "The recorder needs someone in the room; give it ~20 seconds before stopping.",
    },
    {
      id: "recorder",
      status: !rolling ? "todo" : show?.program_connected ? "ok" : waitedTooLong ? "warn" : "wait",
      title: "Program output connected",
      hint: !rolling ? "Turns green once the recorder picks up the program." : waitedTooLong ? "The recorder hasn't connected yet — make sure a source is on air and you're still in the room, then stop and start again." : "Cloudflare's recorder is joining…",
    },
  ];

  const byId = Object.fromEntries(rows.map((r) => [r.id, r.status]));
  const set = ["room", "devices", "source"].every((id) => byId[id] === "ok");   // safe to press Record
  const rollingOk = rolling && !!show?.program_connected;                        // program is being captured
  const warnings = rows.filter((r) => r.status === "warn").length;
  const done = rows.filter((r) => r.status === "ok").length;
  const state = rollingOk ? "rolling" : warnings ? "warn" : set ? "set" : "todo";

  const [open, setOpen] = useState(true);
  useEffect(() => { if (rollingOk) setOpen(false); }, [rollingOk]);

  const frame = {
    rolling: "border-emerald-500/30 bg-emerald-500/5",
    warn: "border-amber-500/30 bg-[#0f1520]",
    set: "border-[#c9772e]/40 bg-[#c9772e]/5",
    todo: "border-white/10 bg-[#0f1520]",
  }[state];
  const pill = {
    rolling: "bg-emerald-500/20 text-emerald-300",
    warn: "bg-amber-500/20 text-amber-300",
    set: "bg-[#c9772e]/20 text-[#e6a25f]",
    todo: "bg-white/10 text-zinc-300",
  }[state];
  const summary = {
    rolling: "Rolling — program captured",
    warn: `${warnings} to fix · ${done}/${rows.length}`,
    set: rolling ? "Recorder joining…" : "Set — press Record",
    todo: `${done}/${rows.length}`,
  }[state];

  return (
    <section className={`rounded-xl border ${frame}`} data-testid="preshow-checklist" data-state={state}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-4 py-3 text-left" data-testid="preshow-toggle">
        <ClipboardCheck className={`h-4 w-4 ${state === "rolling" ? "text-emerald-400" : "text-[#e6a25f]"}`} />
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">Pre-show check</span>
        <span className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold ${pill}`} data-testid="preshow-summary">{summary}</span>
        {open ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
      </button>
      {open && (
        <ul className="space-y-3 border-t border-white/10 px-4 py-4">
          {rows.map((r) => <Row key={r.id} status={r.status} title={r.title} hint={r.hint} testId={`preshow-${r.id}`} />)}
        </ul>
      )}
    </section>
  );
}

export default PreShowChecklist;
